// Dependencies
import { redirect, type Params } from 'react-router-dom';
import {
  ApolloQueryResult,
  Reference,
  StoreObject,
} from '@apollo/client';
import debug from 'debug';

// Types
import { OrderType } from '../../../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';
import {
  VehicleOwnership,
  VehicleUsage,
} from '../../../../../../types/vehicles';

// Constants
import {
  directoryNaming,
  formAccidentNaming,
} from '../../../../../../components/forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../../../../../../components/forms/AddVehicleForm/constants';

// GQL
import { client } from '../../../../../../gql/client';
// FOLD (At.0001979.0008.02): UPDATE_ACCIDENT_ORDER_RELATIONSHIPS y las
// mutations de VehicleAccidentContext ya no se usan — vehículos+contexts+
// satélites+fotos viajan dentro de SAVE_ACCIDENT (tx única).
import {
  DELETE_MANY_IMAGES,
  UPDATE_NEW_ATTENTION_ACCIDENT,
  SAVE_ACCIDENT,
} from '../../../../../../gql/mutations/accidents';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import {
  GET_VEHICLE_BY_ID,
  GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
} from '../../../../../../gql/queries/vehicles';
import { FIND_OR_CREATE_VEHICLE_BY_PLATE } from '../../../../../../gql/mutations/vehicles';
import { GET_ACCIDENT_BY_ID } from '../../../../../../gql/queries/accidents/accidents';
import { GET_ATTENTION_ACCIDENT_BY_ID } from '../../../../../../gql/queries/accidents/attentionAccidents';

// Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import {
  assertDraftOwner,
  DraftOwnerMismatchError,
} from '../../../../../../utils/draftOwnership';
import { ACCIDENT_EDIT_SCOPE } from '../../../../../../utils/draftScopes';
import {
  ownSketchFileName,
  readAccidentNavigationContext,
  releaseAccidentEditDraft,
  resolveAccidentEditRedirect,
  sketchFileOfEdit,
  type AccidentServerParent,
} from '../../../../../../utils/accidentEditDraft';
import { screenFormContext, type FormContext } from '../../../../../../offline/formStorage';
import {
  difference,
  splitDifference,
  removeArraysAtFirstLevel,
} from '../../../../../../utils/difference';
import {
  compareImageJSONs,
  compareSketchJson,
  UploadImage,
  CloudinaryUploadError,
} from '../../../../../../utils/image';
import { getClassificationCode } from '../../../../../../utils/orders';
import {
  addImageUploadOperation,
  OFFLINE_PENDING_UPLOADS_DIR,
} from '../../../../../../offline/offlineImageUploadQueue';
import { addCompoundOperation } from '../../../../../../offline/offlineCompoundQueue';
import { isOnlineNow } from '../../../../../../offline/connectivity';
import { isActuallyOnline } from '../../../../../../offline/checkConnectivity';
import { createOfflineQueuedError } from '../../../../../../offline/isOfflineQueuedError';
import {
  createPhaseDeadline,
  phaseScopedClient,
  raceWithDeadline,
  isTransportFailure,
} from '../../../../../../offline/phase2Transport';
import { enqueuePhotosForUpload } from '../../../../../../utils/enqueuePhotos';
import { captureLocalImages } from '../../../../../../offline/offlineImageStore';
import { saveFilesIntoADirectory } from '../../../../../../utils/files';
import { getSketchFromSessionStorage } from '../../../../../../utils/sketch';
import { pickSketchFile } from '../../../../../../utils/sketchFile';
import i18n from '../../../../../../i18n';


// Sentry
import * as Sentry from '@sentry/react';

// Redux
import { store } from '../../../../../../state/configureStore';
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { getCursor } from '../../../../../../state/selectors/ui/tables';

// Configure debug namespaces
const logMain = debug('rois:accident:save');
const logVehicles = debug('rois:accident:vehicles');
const logRelations = debug('rois:accident:relations');
const logCache = debug('rois:accident:cache');
const logError = debug('rois:accident:error');

// Enable logs only in development
if (import.meta.env.DEV) {
  // You can use localStorage to activate all logs in development
  debug.enable('rois:accident:*');

  // Initialization messages
  logMain('Debug activado para módulo de accidentes');
}

/**
 * OfflineQueueLink lanza `Error('Mutation queued for offline sync')` con flag
 * `offlineQueued: true` cuando una mutation se encola sin red. Apollo lo
 * envuelve en ApolloError. Mismo helper que NewOrder/NewCommunication etc.
 */
function isOfflineQueuedError(err: unknown): boolean {
  const e = err as {
    offlineQueued?: boolean;
    message?: string;
    networkError?: { offlineQueued?: boolean };
  };
  return (
    e?.offlineQueued === true ||
    e?.networkError?.offlineQueued === true ||
    (e?.message ?? '').includes('Mutation queued for offline sync')
  );
}

// Helper function to normalize IDs (can be string or object with id property)

const normalizeId = (item: any): string => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item && typeof item === 'object' && item.id ? item.id : '';
};

/**
 * Detección robusta del error "Vehicle not found" del backend.
 *
 * `vehicleByPlate` resolver usa `prisma.vehicle.findFirstOrThrow` que lanza
 * un error con mensaje que ha cambiado entre versiones de Prisma:
 *  - Legacy (< Prisma 5): `"No Vehicle found"`.
 *  - Prisma 5/6 actual: `"No Vehicle found"` o `"...findFirstOrThrow..."`.
 *  - GraphQL puede envolver el error en `error.graphQLErrors[0].message` o
 *    `error.networkError.result.errors[0].message`.
 *
 * El `error.message.includes('No Vehicle found')` que se usaba antes era
 * frágil: si Prisma cambia el wording (o se traduce) el catch no detecta y
 * la mutation propaga sin crear el vehículo. **Bug latente desde Bloque C
 * pre-existente** (existe también en main + beta) — afloraba al añadir un
 * remolque nuevo en EditAccident: el GET del trailer fallaba, el catch no
 * capturaba y el trailer nunca se creaba.
 *
 * Esta función mira en todas las ubicaciones donde Apollo puede haber
 * propagado el mensaje y aplica un regex laxo. Si Prisma cambia otra vez,
 * la detección sigue funcionando.
 */
const isVehicleNotFoundError = (error: unknown): boolean => {
  const messages = [
    (error as { message?: string })?.message,
    (error as { graphQLErrors?: Array<{ message?: string }> })
      ?.graphQLErrors?.[0]?.message,
    (
      error as {
        networkError?: { result?: { errors?: Array<{ message?: string }> } };
      }
    )?.networkError?.result?.errors?.[0]?.message,
  ].filter((m): m is string => typeof m === 'string');
  return messages.some(
    (m) =>
      // Mensaje legacy del resolver custom (NestJS) — pre Prisma 5.
      /no\s+vehicle\s+found/i.test(m) ||
      /no\s+vehicle/i.test(m) ||
      // Prisma 5/6 envuelve `findFirstOrThrow` con este texto:
      //   "Invalid `prisma.vehicle.findFirstOrThrow()` invocation ...
      //    An operation failed because it depends on one or more records
      //    that were required but not found. No record was found for a query."
      /findfirstorthrow/i.test(m) ||
      /no\s+record\s+was\s+found\s+for\s+a\s+query/i.test(m) ||
      // Genérico: cualquier "not found" en error de query Prisma.
      /required\s+but\s+not\s+found/i.test(m),
  );
};

// Define auxiliary relationship management functions at module level so they are accessible throughout the file

let vehicleRelationships: any[] = [];

// Helper function to manage vehicle relationships

const updateRelationshipsGlobal = (newVehicles: any[] = []) => {
  debug.enable('rois:accident:*');
  logRelations('🔄 Actualizando relaciones de vehículos en memoria...');

  // Update vehicles relationships
  if (newVehicles.length > 0) {
    logVehicles(
      `🚗 Añadiendo ${newVehicles.length} nuevas relaciones de vehículos`
    );
    newVehicles.forEach((vehicle) => {
      const vehicleId = normalizeId(vehicle);
      // Verify if it already exists to avoid duplicates
      const exists = vehicleRelationships.some(
        (existing) => normalizeId(existing) === vehicleId
      );
      if (!exists && vehicleId) {
        vehicleRelationships.push(vehicleId);
        logVehicles(`✅ Añadida relación de vehículo: ${vehicleId}`);
      } else {
        logVehicles(`ℹ️ La relación de vehículo ya existe: ${vehicleId}`);
      }
    });
  }

  logRelations('📊 Estado actual de relaciones:');
  logRelations('🚗 Relaciones de vehículos:', vehicleRelationships.length);
};

// Function to remove relationships
const removeRelationshipsGlobal = (vehicleIds: string[] = []) => {
  debug.enable('rois:accident:*');
  logRelations('🗑️ Eliminando relaciones en memoria...');

  // Remove vehicles relationships
  if (vehicleIds.length > 0) {
    logVehicles(`🚗 Eliminando ${vehicleIds.length} relaciones de vehículos`);
    logVehicles('📊 IDs de vehículos a eliminar:', vehicleIds);
    const initialLength = vehicleRelationships.length;
    vehicleRelationships = vehicleRelationships.filter(
      (vehicle) => !vehicleIds.includes(normalizeId(vehicle))
    );
    logVehicles(
      `✅ Eliminadas ${
        initialLength - vehicleRelationships.length
      } relaciones de vehículos`
    );
  }

  logRelations('📊 Estado actual de relaciones tras eliminación:');
  logRelations('🚗 Relaciones de vehículos:', vehicleRelationships.length);

  // Show detailed IDs of vehicles that remain
  if (vehicleRelationships.length > 0) {
    logVehicles('📋 Lista de IDs de vehículos después de la eliminación:');
    vehicleRelationships.forEach((vehicle) => {
      const vehicleId =
        typeof vehicle === 'string' ? vehicle : normalizeId(vehicle);
      logVehicles(`  - Vehículo ID: ${vehicleId}`);
    });
  }
};

/**
 * Captura el asistente de editar accidente y lo encola como compound
 * `accident-edit` (spec 2026-09-11 § 1 E4). Lo comparten la rama sin red y el
 * camino nuevo del plazo vencido; el replay es `syncAccidentEdit`.
 */
async function queueAccidentEdit(
  orderId: string,
  accidentRoid: string,
  form: FormContext = screenFormContext,
  // El guardado en vivo ya entregó las fotos nuevas a la bandeja: la operación
  // las lleva igual, pero su reintento no las repite (spec
  // 2026-10-01-photo-outbox-identity § 10.4 E5).
  {
    photosHandedOff = false,
    serverParent = null,
  }: {
    photosHandedOff?: boolean;
    /** El padre según el servidor (copia del paso 1): destino si el contexto no es de este accidente. */
    serverParent?: AccidentServerParent | null;
  } = {},
): Promise<{ redirectTo: string }> {
  const sessionSnapshots: Record<string, string | null> = {};
  Object.values(formAccidentNaming).forEach((key) => {
    sessionSnapshots[key] = form.store.getItem(key);
  });
  Object.values(formVehicleNaming).forEach((key) => {
    sessionSnapshots[key] = form.store.getItem(key);
  });

  const localImages = await captureLocalImages([
    directoryNaming.CANVAS, // 'canvas-accident'
    'accident',             // fotos normales
  ]);

  await addCompoundOperation({
    type: 'accident-edit',
    parentOrderId: orderId, // para el redirect/toast; el id real va en snapshots
    parentOrderRoid: accidentRoid,
    isCampaignParent: false,
    sessionSnapshots,
    localImages,
    ...(photosHandedOff && { photosHandedOff: true }),
  });

  // Solo lo de esta edición: ni el alta de accidente a medias ni las carpetas
  // compartidas (ver `releaseAccidentEditDraft`). Del croquis, el fichero que
  // acaba de capturar la operación (el reintento lo sube desde su copia). El
  // contexto de navegación se queda: decide a dónde se vuelve.
  const sketchFileName = await sketchFileOfEdit(form);
  await releaseAccidentEditDraft(form, orderId, { sketchFileName });
  const redirectTo = resolveAccidentEditRedirect({
    orderId,
    navigationContext: readAccidentNavigationContext(form.store),
    serverParent,
  });
  return { redirectTo };
}

// `form`: de dónde leer y escribir el formulario. Por defecto, la pantalla; el
// reintento de la cola pasa uno en memoria con los datos de la operación, para no
// tocar el formulario abierto (spec outbox-autocontenido v2 § 5).
// `params`: los de la ruta en el guardado en vivo (`:id` = la Order del
// accidente de la URL). El reintento de la cola no los tiene ni los necesita.
export const saveData = async (opts?: {
  clientOpId?: string;
  isReplay?: boolean;
  form?: FormContext;
  params?: Params;
}) => {
  const form = opts?.form ?? screenFormContext;
  debug.enable('rois:accident:*');
  logMain('🔍 INICIANDO PROCESO DE GUARDADO Y ACTUALIZACIÓN DE DATOS');

  // Plazo de UNA fase (E1): un solo AbortController para las 8 llamadas y la
  // subida de imágenes. Sin él, con red degradada esto esperaba sin techo.
  const deadline = createPhaseDeadline();
  const phaseClient = phaseScopedClient(client, deadline);
  let writesCommitted = false;
  // Las fotos nuevas ya están en la bandeja (ver `queueAccidentEdit`).
  let photosHandedOff = false;
  // El fichero de croquis que se lleva este guardado (subido o rescatado):
  // al terminar se borra por nombre de `canvas-accident` (ver
  // `ownSketchFileName`).
  let ownSketchFile: string | null = null;

  const newAccidentForm = form.store.getItem(
    formAccidentNaming.EDIT_ACCIDENT_FORM
  );

  const accidentDataForm = JSON.parse(
    decompressSessionStorage(newAccidentForm as string)
  );

  const accidentId = accidentDataForm.accidentId;
  const attentionAccidentId = accidentDataForm.attentionAccidentId;
  const orderId = accidentDataForm.orderId;
  // El padre que dio el servidor al abrir el paso 1: a dónde se vuelve si el
  // contexto de navegación no es de este accidente.
  const serverParent: AccidentServerParent = {
    id: accidentDataForm.parentOrderIdFromData,
    type: accidentDataForm.parentOrderTypeFromData,
  };

  // 🔴 Barrera (R5; invariante I3 del spec 2026-11-02 wizard-draft-ownership):
  // el guardado escribe en el accidente de la URL, nunca en el de la copia.
  // Antes `orderId`/`accidentId`/`attentionAccidentId` salían SOLO de la copia
  // del paso 1: con atrás desde el lápiz de B, la URL de editar A guardaba en
  // B. Dos comprobaciones, las dos solo en vivo (el reintento de la cola
  // trabaja con su copia en memoria y no gana rechazos nuevos,
  // `.claude/rules/guard-nuevo-cola-offline.md`):
  //  1. Las copias son de la URL (dueño que dejan los loaders).
  //  2. La Order de la copia es la de la URL.
  // No debería saltar nunca —los loaders descartan las copias ajenas y la
  // pantalla remonta—; si salta no se escribe NI se encola nada. Lanza
  // `DraftOwnerMismatchError` con la etiqueta de Sentry
  // `draft-owner-mismatch:accident-edit`.
  const isReplay = opts?.isReplay === true;
  const urlOrderId = opts?.params?.id;
  assertDraftOwner(ACCIDENT_EDIT_SCOPE, urlOrderId, { isReplay }, form.store);
  if (!isReplay && orderId !== urlOrderId) {
    const mismatch = new DraftOwnerMismatchError(
      ACCIDENT_EDIT_SCOPE.name,
      orderId ?? null,
      urlOrderId ?? '(sin :id)'
    );
    Sentry.captureException(mismatch, {
      tags: { guard: `draft-owner-mismatch:${ACCIDENT_EDIT_SCOPE.name}` },
    });
    throw mismatch;
  }

  // ── OFFLINE BRANCH (espejo del CREAR): capturar todo y encolar compound ──
  // Sin esto, el editar offline disparaba N mutations encadenadas y la PRIMERA
  // encolada (p.ej. FindOrCreateVehicleByPlate) lanzaba offlineQueued → el catch
  // redirigía como "éxito" → SAVE_ACCIDENT (campos+vehículos) nunca se ejecutaba
  // ni encolaba → pérdida de datos (At.0001979.0008.02, vertiente edición).
  // Sondeo previo (E2/E3): con cobertura mala `navigator.onLine` dice `true`.
  const online = isOnlineNow();
  const reachable = online && !isReplay ? await isActuallyOnline() : online;
  if (!reachable) {
    if (isReplay) {
      // Replay ya trae localImages/sessionSnapshots restaurados desde la op
      // compound original (ver syncAccidentEdit). Volver a encolar aquí crearía
      // una op NUEVA con op.id distinto → clientOpId distinto → duplicados al
      // reconectar. Lanzamos para que el sync manager la trate como transient
      // y reintente la MISMA op compound.
      throw new Error('Replay sin conexión — se reintentará');
    }
    logMain('📴 Modo offline — encolando EDICIÓN de accidente para sync posterior');

    const { redirectTo } = await queueAccidentEdit(
      orderId,
      accidentDataForm.roid ?? '',
      form,
      { serverParent },
    );
    window.dispatchEvent(new CustomEvent('offline-accident-queued'));
    // Centinela compartido: el action avisa con toast y redirige (E6).
    throw createOfflineQueuedError({ redirectTo });
  }

  // Redux data
  const dispatch = store.dispatch;
  const cursorMultimedia = getCursor(tableIds.MULTIMEDIA)(store.getState());

  try {
    const basicInfoAccidentForm = form.store.getItem(
      formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM
    );

    const basicInfoAccidentDataForm = JSON.parse(
      decompressSessionStorage(basicInfoAccidentForm as string)
    );

    const basicInfoToCompare = basicInfoAccidentDataForm.dataToCompare;
    delete basicInfoAccidentDataForm.dataToCompare;

    const basicInfoAccidentDataFormClean = removeArraysAtFirstLevel(
      basicInfoAccidentDataForm
    );

    const vehiclesForm = form.store.getItem(
      formVehicleNaming.EDIT_VEHICLE_FORM
    );

    const vehiclesDataForm = JSON.parse(
      decompressSessionStorage(vehiclesForm as string)
    );

    const vehiclesDataToCompare = vehiclesDataForm.dataToCompare;

    vehiclesDataToCompare?.letters &&
      vehiclesDataToCompare?.letters.length > 0 &&
      (vehiclesDataToCompare.letters = JSON.parse(
        vehiclesDataToCompare.letters
      ));

    delete vehiclesDataForm.dataToCompare;
    delete vehiclesDataForm.orderParentRoid;
    delete vehiclesDataForm.orderParentId;
    delete vehiclesDataForm.intentVehicles;

    vehiclesDataForm?.letters &&
      vehiclesDataForm?.letters.length > 0 &&
      (vehiclesDataForm.letters = JSON.parse(vehiclesDataForm.letters));

    const accidentDataToCompare = accidentDataForm.dataToCompare;
    delete accidentDataForm.dataToCompare;
    delete accidentDataForm.intent;
    const accidentDataFormClean = removeArraysAtFirstLevel(accidentDataForm);

    const basicInfoDifference: any = difference(
      basicInfoAccidentDataFormClean,
      basicInfoToCompare
    );

    const vehiclesDifference: any = splitDifference(
      vehiclesDataForm,
      vehiclesDataToCompare
    );

    const accidentDataDifference: any = difference(
      accidentDataFormClean,
      accidentDataToCompare
    );

    const { sketchToSaveOrUpdate, sketchToRemove } = compareSketchJson(
      basicInfoAccidentDataFormClean.sketch,
      basicInfoToCompare.sketch
    );

    const { imagesToSave, imagesToRemove } = compareImageJSONs(
      basicInfoToCompare.images,
      basicInfoAccidentDataFormClean.images
    );

    // Get order data for image naming
    let orderMetadata = null;
    if ((imagesToSave || []).length || sketchToSaveOrUpdate) {
      const { data: orderData } = await phaseClient.query({
        query: GET_ORDER_BY_ID,
        variables: { id: orderId },
        fetchPolicy: 'cache-first',
      });

      if (orderData?.order) {
        orderMetadata = {
          indexExpedition: orderData.order.indexExpedition,
          indexType: orderData.order.indexType || 0,
          classification: orderData.order.classificationIDs?.[0]
            ? await getClassificationCode(orderData.order.classificationIDs[0])
            : '01',
          orderType: OrderType.Accident,
        };
      }
    }

    // Offline-safe sketch upload: si Cloudinary falla (sin red, 4xx, 5xx)
    // y el caller tiene `sketchToSaveOrUpdate`, se persiste el blob en OPFS
    // y se encola en `offlineImageUploadQueue`. El DELETE del sketch viejo
    // (sketchToRemove) NO se ejecuta hasta que la subida diferida termine OK.
    let imageArray: any[] = [];
    let cloudinaryFailedForSketch = false;
    // OUTBOX DE FOTOS (2026-08): se encolan SIEMPRE, no solo en la rama de
    // rescate offline que ya existía debajo. El borrado de imágenes de este
    // fichero usa `imagesToDelete` (lista explícita del formulario), no el
    // patrón `removeImages`, así que no depende del plan.
    if ((imagesToSave || []).length && orderMetadata) {
      await enqueuePhotosForUpload({
        picturesDirectoryName: OrderType.Accident,
        orderId,
        orderType: OrderType.Accident,
        entityType: 'accident',
        orderMetadata,
        // 🔴 SOLO las nuevas (`compareImageJSONs` ya calcula la diferencia
        // contra las que había). Sin este filtro se re-suben las que ya están
        // en BD y se duplican los registros Image.
        onlyNames: (imagesToSave as Array<{ name: string }>).map(
          (i) => i.name,
        ),
        images: form.images,
        // En un reintento de la cola: si un intento anterior ya las entregó,
        // no se repiten (spec 2026-10-01-photo-outbox-identity § 10.4 E4).
        handoff: form.photoHandoff,
      });
      photosHandedOff = true;
    }

    if ((imagesToSave || []).length || sketchToSaveOrUpdate) {
      // 🔴 El fichero de SU croquis, por nombre (Ruling 15; spec 2026-11-02
      // § 6): `canvas-accident` la comparten las pestañas y crear y editar
      // accidente, y se subía «el primero de la carpeta» aunque fuera el
      // croquis de otra. Marca `fileName` → ese; `null` → ninguno; sin marca
      // y dibujo local (borrador u operación encolada de antes) → el primero,
      // lo de siempre. Se mira ANTES de subir y con la misma fuente que
      // `UploadImage` (en el reintento, la copia de la operación).
      if (sketchToSaveOrUpdate) {
        ownSketchFile = await ownSketchFileName(
          basicInfoAccidentDataFormClean.sketch,
          form.images
        );
      }
      try {
        // La subida corre contra el plazo de la fase.
        imageArray = await raceWithDeadline(
          UploadImage(
          undefined,
          ownSketchFile ? directoryNaming.CANVAS : undefined,
          orderMetadata || undefined,
          {
            throwOnError: true,
            orderId,
            entityType: 'accident',
            formImages: form.images,
            formStore: form.store,
            canvasFileName: ownSketchFile,
          },
          ),
          deadline,
        );
      } catch (uploadErr) {
        // Rescate offline al EDITAR (bug At.0001979.0008.02, vertiente edición):
        // antes SOLO el croquis se encolaba; una FOTO NORMAL caía en el `else` y
        // se re-lanzaba → el throw ocurría ANTES del SAVE_ACCIDENT, así que NI la
        // foto NI los campos editados se encolaban → pérdida TOTAL del guardado
        // offline. El flujo de CREAR sí rescataba todo (rama offline temprana).
        // Ahora, con red caída, encolamos croquis Y fotos en offlineImageUploadQueue
        // (kind 'sketch'/'photo' — ambos soportados por syncImageUploadQueue) y NO
        // re-lanzamos: el flujo llega al SAVE_ACCIDENT, que OfflineQueueLink encola,
        // y el catch de offline hace el redirect limpio. Al reconectar sincronizan
        // las dos colas (fotos → Hetzner + CREATE_NEW_IMAGE; campos → SaveAccident).
        // Fuente única (E3).
        const isOffline = !isOnlineNow();
        const hasRegularPhotos = (imagesToSave || []).length > 0;
        const canRescueOffline =
          // En replay las imágenes ya viven en op.localImages de la compound
          // (restauradas por syncAccidentEdit) — rescatarlas aquí duplicaría por
          // doble contabilidad compound×cola de imágenes. Re-lanzar en su lugar.
          !opts?.isReplay &&
          uploadErr instanceof CloudinaryUploadError &&
          !!orderMetadata &&
          (sketchToSaveOrUpdate || (isOffline && hasRegularPhotos));

        if (!canRescueOffline) {
          // Error online real (4xx/5xx con red) o sin metadata → re-lanzar para
          // que el manejo de errores habitual lo muestre.
          throw uploadErr;
        }
        // Narrow para TS: canRescueOffline ya exige orderMetadata truthy.
        if (!orderMetadata) throw uploadErr;

        let rescuedSomething = false;
        try {
          // (a) Croquis — comportamiento previo intacto.
          if (sketchToSaveOrUpdate) {
            cloudinaryFailedForSketch = true;
            let files: { file: File; id: string }[] = [];
            try {
              files = await form.images.list(directoryNaming.CANVAS);
            } catch {
              files = [];
            }
            // El de este borrador, por nombre (nunca «el primero»).
            const sourceFile = ownSketchFile
              ? pickSketchFile(files, ownSketchFile)?.file
              : undefined;
            logMain(
              '🌐 Cloudinary failed offline — encolando sketch (sourceFile=%s)',
              sourceFile ? `${sourceFile.name} ${sourceFile.size}B` : 'NONE',
            );
            if (sourceFile) {
              const blobFileName = `pending_sketch_${orderId}_${Date.now()}.png`;
              // CRÍTICO: el blob va a un directorio dedicado, NO a
              // `directoryNaming.CANVAS`: esa carpeta es del formulario (la
              // vacía el croquis siguiente o la puerta de «Crear accidente»)
              // y destruiría el blob antes del sync.
              await saveFilesIntoADirectory(OFFLINE_PENDING_UPLOADS_DIR, [
                new File([sourceFile], blobFileName, {
                  type: sourceFile.type || 'image/png',
                }),
              ]);
              const sketchData = getSketchFromSessionStorage('accident', form.store);
              const queuedId = await addImageUploadOperation({
                kind: 'sketch',
                orderId,
                orderType: OrderType.Accident,
                blobDirectory: OFFLINE_PENDING_UPLOADS_DIR,
                blobFileName,
                orderMetadata,
                sketchData: {
                  svgContent: sketchData.svgContent,
                  fabricJson: sketchData.fabricJson,
                },
                sketchToRemoveId: sketchToRemove?.id,
              });
              rescuedSomething = true;
              logMain(
                '📦 sketch encolado offline id=%s blob=%s/%s sketchToRemove=%s',
                queuedId,
                OFFLINE_PENDING_UPLOADS_DIR,
                blobFileName,
                sketchToRemove?.id ?? 'none',
              );
            } else {
              logError(
                '⚠️  Sin blob fuente OPFS — conservamos el sketch viejo, perdemos la edición',
              );
            }
          }

          // (b) Fotos normales: ya están en la bandeja —se entregan ARRIBA,
          // antes de subir el croquis—, así que cuentan como rescatadas y el
          // control de flujo queda como estaba. Antes se volvían a encolar
          // TODAS las de la carpeta del formulario, y en edición esa carpeta
          // también tiene las que ya están en el servidor: se duplicaban
          // (spec 2026-10-01-photo-outbox-identity § 10.2).
          if (isOffline && hasRegularPhotos && photosHandedOff) {
            rescuedSomething = true;
            logMain('📦 fotos ya en la bandeja; el rescate solo encola el croquis');
          }

          if (rescuedSomething) {
            window.dispatchEvent(
              new CustomEvent('offline-sync-error', {
                detail: { message: i18n.t('errors:sketchPendingUpload') },
              }),
            );
          }
        } catch (queueErr) {
          Sentry.captureException(queueErr);
          logError('⚠️  Error encolando imagen offline: %O', queueErr);
          // Si NO pudimos rescatar NADA, re-lanzar para no fingir éxito y perder
          // datos en silencio (mejor que el usuario vea el fallo).
          if (!rescuedSomething) {
            throw uploadErr;
          }
        }
        // Rescate OK → NO re-lanzar: el flujo continúa hasta el SAVE_ACCIDENT
        // (campos editados → OfflineQueueLink lo encola → catch offline redirige
        // limpio). Las imágenes quedan en su cola y sincronizan al reconectar.
      }
    }

    // Fold (Bloque B2): la CREACIÓN de Image ya no se hace con CREATE_NEW_IMAGE
    // (mutation separada post-save). El path update tiene el `orderId` real
    // desde el inicio, así que las fotos ya subidas a Hetzner (imageArray, con
    // su `storageKey`) se pliegan en el input de SAVE_ACCIDENT (campo `images`),
    // y el backend crea las Image DENTRO de su $transaction atómica.
    // `saveAccident.order` (NewOrder) devuelve `imageIDs` actualizado → Apollo
    // normaliza la celda Order:<id>; los refetch network-only posteriores
    // (GET_ORDER_BY_ID / GET_ACCIDENT_BY_ID) repueblan el resto.
    const newImages = imageArray
      .filter((image) => !!image.storageKey)
      .map((image) => ({
        storageKey: image.storageKey,
        name: image.name,
        creatingDate: image.creatingDate,
        sketchSvg: image.sketchSvg ?? null,
        sketchFabricJson: image.sketchFabricJson ?? null,
      }));

    // El BORRADO sigue como mutation separada (DELETE_MANY_IMAGES) — fuera de
    // scope del fold. Este array ya solo acumula el DELETE (más abajo).
    const imageArrayWithDeletes: Promise<unknown>[] = [];

    const deletedImages = imagesToRemove?.length;
    const addedImages = imagesToSave?.length;

    client.cache.modify({
      fields: {
        orders(existingOrders = {}, { readField }) {
          const newOrderRef = {
            __ref: `Order:${orderId}`,
          };

          const images: readonly string[] | undefined = readField(
            'imageIDs',
            newOrderRef
          );

          if (images?.length === 0 && addedImages > 0) {
            const order = client.readQuery({
              query: GET_ORDER_BY_ID,
              variables: {
                id: orderId,
              },
            });

            let mostCloseOrder: undefined | string;

            const dateOrder = new Date(order.order.creationDate);

            for (let i = 0; i < existingOrders?.orders?.length - 1; i++) {
              const idOrder = existingOrders?.orders[i]?.__ref.split(':')[1];

              const currentOrder = client.readQuery({
                query: GET_ORDER_BY_ID,
                variables: {
                  id: idOrder,
                },
              });
              const currentOrderDate = new Date(
                currentOrder?.order?.creationDate
              );

              if (dateOrder.getTime() > currentOrderDate.getTime()) {
                mostCloseOrder = existingOrders?.orders[i]?.__ref.split(':')[1];
                break;
              }
            }

            if (!mostCloseOrder) {
              return {
                ...existingOrders,
              };
            }

            const mostCloseOrderIndex = existingOrders.orders.findIndex(
              (item: Reference | StoreObject) =>
                readField('id', item) === mostCloseOrder
            );
            const existingItems = existingOrders.orders.filter((item: any) => item !== null);

            existingItems.splice(mostCloseOrderIndex, 0, newOrderRef);

            const newOrdersArray = existingItems;

            const setCursorMultimedia = {
              ...cursorMultimedia,
              take: takeBase,
              cursor: '',
              orderBy: 'desc',
            };

            if (!opts?.isReplay && existingOrders?.orders?.length > 0) {
              dispatch(setCursor(tableIds.MULTIMEDIA, setCursorMultimedia));
            }
            return {
              ...existingOrders,
              orders: newOrdersArray,
              totalCount: existingOrders.totalCount + 1,
              pageInfo: {},
            };
          } else {
            return {
              ...existingOrders,
            };
          }
        },
      },
    });

    // Invariante offline: si Cloudinary falló para el sketch nuevo,
    // NO borramos el viejo aún — quedará pendiente del sync diferido
    // (offlineImageUploadQueue) para mantener "nunca borrar sin haber creado".
    // Y si había croquis nuevo pero no llegó a subirse (sin fichero en la
    // carpeta: la subida no produce nada), tampoco: se perdería el croquis
    // (spec outbox-autocontenido v2 § 5.4).
    const newSketchUploaded = imageArray.some(
      (img: Record<string, unknown>) => 'sketchSvg' in img,
    );
    const imagesToRemoveIds = [
      ...(imagesToRemove?.map((image) => image.id) ?? []),
      ...(sketchToRemove?.id &&
      !cloudinaryFailedForSketch &&
      (!sketchToSaveOrUpdate || newSketchUploaded)
        ? [sketchToRemove.id]
        : []),
    ];

    imagesToRemoveIds?.length
      ? imageArrayWithDeletes.push(
          phaseClient.mutate({
            mutation: DELETE_MANY_IMAGES,
            variables: {
              ids: imagesToRemoveIds,
            },
            update(cache, { data: { deleteManyImagesByIds } }) {
              deleteManyImagesByIds.filter(Boolean).forEach(
                (image: { id: Reference | StoreObject }) => {
                  cache.modify({
                    id: cache.identify(image.id),
                    fields: {
                      images(_, { DELETE }) {
                        return DELETE;
                      },
                    },
                  });
                }
              );
            },
          })
        )
      : undefined;

    client.cache.modify({
      fields: {
        orders(existingOrders = {}, { readField }) {
          const newOrderRef = {
            __ref: `Order:${orderId}`,
          };
          const images: readonly string[] | undefined = readField(
            'imageIDs',
            newOrderRef
          );
          if (
            (images?.length ?? 0) + addedImages - deletedImages === 0 &&
            existingOrders.orders.some((order: StoreObject | Reference) => {
              const ref = readField('id', order);
              return ref === orderId;
            })
          ) {
            const filterOrders = existingOrders.orders.filter((order: any) => {
              if (order === null) return false;
              const ref = readField('id', order as StoreObject | Reference);
              return ref !== orderId;
            });

            const setCursorMultimedia = {
              ...cursorMultimedia,
              take: takeBase,
              cursor: '',
              orderBy: 'desc',
            };

            if (!opts?.isReplay && existingOrders?.orders?.length > 0) {
              dispatch(setCursor(tableIds.MULTIMEDIA, setCursorMultimedia));
            }

            return {
              ...existingOrders,
              orders: filterOrders,
              totalCount: existingOrders.totalCount - 1,
              pageInfo: {},
            };
          } else {
            return {
              ...existingOrders,
            };
          }
        },
      },
    });

    await Promise.all(imageArrayWithDeletes);

    // ══ FOLD contexts (At.0001979.0008.02): set COMPLETO deseado de vehículos
    // construido ANTES del saveAccident. El backend reconcilia la side-table
    // VehicleAccidentContext DENTRO de su tx (borra contexts de vehículos
    // ausentes, actualiza snapshots de los presentes, crea nuevos + cadena de
    // trailers). Sustituye los diffs added/modified/removed + 7
    // FIND_OR_CREATE_VEHICLE_ACCIDENT_CONTEXT + deletes + el
    // UPDATE_ACCIDENT_ORDER_RELATIONSHIPS final (cadena no-atómica).
    const switchValue = JSON.parse(vehiclesDataForm.involvedVehiclesSwitch);
    const originalSwitchValue = vehiclesDataToCompare?.involvedVehiclesSwitch
      ? JSON.parse(vehiclesDataToCompare.involvedVehiclesSwitch)
      : false;
    const switchTurnedOff =
      originalSwitchValue === true && switchValue === false;
    const vehiclesChanged =
      switchTurnedOff ||
      (vehiclesDifference.added?.letters?.length ?? 0) > 0 ||
      (vehiclesDifference.added?.registTrailer?.length ?? 0) > 0 ||
      (vehiclesDifference.removed?.letters?.length ?? 0) > 0 ||
      (vehiclesDifference.removed?.registVehicle?.length ?? 0) > 0 ||
      (vehiclesDifference.removed?.registTrailer?.length ?? 0) > 0 ||
      Object.keys(vehiclesDifference.modified?.vehicles || {}).length > 0;

    // Satélites (antes viajaban en UPDATE_ACCIDENT_ORDER_RELATIONSHIPS).
    const satellitesChanged = !!(
      basicInfoDifference?.surveillanceBody ||
      basicInfoDifference?.weather ||
      basicInfoDifference?.surfaceCondition ||
      basicInfoDifference?.mediumKnowledge ||
      basicInfoDifference?.luminosity ||
      basicInfoDifference?.conditionsDriving
    );

    // undefined → el backend no toca vehículos/contexts.
    let vehiclesInput:
      | Array<{
          vehicleId: string;
          model?: string | null;
          color?: string | null;
          insurance?: string | null;
          policyNumber?: string | null;
          numberOccupants?: number;
          isTrailer: boolean;
          isActive: boolean;
          parentVehicleId?: string;
        }>
      | undefined;
    const processedVehicleIds: string[] = [];
    if (switchTurnedOff) {
      // Switch ON→OFF: `[]` → el backend borra todos los contexts y desconecta
      // los vehículos en la tx. Las entidades Vehicle NO se borran: son
      // maestros find-or-create por matrícula, compartibles entre accidentes —
      // el antiguo DELETE_ALL_VEHICLES podía romper contexts de OTRO accidente.
      vehiclesInput = [];
    } else if (switchValue && vehiclesChanged) {
      const desiredVehicles = (
        Object.values(vehiclesDataForm.vehicles || {}) as any[]
      ).filter((v) => v?.registVehicle);
      vehiclesInput = [];
      for (const vehicle of desiredVehicles) {
        // find-or-create idempotente: matrícula existente → mismo Vehicle;
        // matrícula nueva/cambiada → entidad nueva (la vieja sale del set →
        // el backend borra su context y la desconecta en el reconcile).
        const dataVehicle = await phaseClient.mutate({
          mutation: FIND_OR_CREATE_VEHICLE_BY_PLATE,
          variables: {
            input: {
              plate: vehicle.registVehicle,
              model: vehicle.modelVehicle || null,
              color: vehicle.colorVehicle || null,
              insurance: vehicle.insurerVehicle || null,
              policyNumber: vehicle.policyVehicle || null,
              isTrailer: false,
              isActive: false,
              ownership: VehicleOwnership.INTERNAL,
              usage: VehicleUsage.ACCIDENT,
            },
          },
        });
        const mainVehicleId = dataVehicle.data.findOrCreateVehicleByPlate.id;
        processedVehicleIds.push(mainVehicleId);
        vehiclesInput.push({
          vehicleId: mainVehicleId,
          model: vehicle.modelVehicle || null,
          color: vehicle.colorVehicle || null,
          insurance: vehicle.insurerVehicle || null,
          policyNumber: vehicle.policyVehicle || null,
          numberOccupants: parseInt(vehicle.occupantsVehicle) || 0,
          isTrailer: false,
          isActive: false,
        });
        if (vehicle.registTrailer && vehicle.registTrailer.trim() !== '') {
          const dataTrailer = await phaseClient.mutate({
            mutation: FIND_OR_CREATE_VEHICLE_BY_PLATE,
            variables: {
              input: {
                plate: vehicle.registTrailer,
                insurance: vehicle.insurerTrailer || null,
                policyNumber: vehicle.policyTrailer || null,
                isTrailer: true,
                isActive: false,
                ownership: VehicleOwnership.INTERNAL,
                usage: VehicleUsage.ACCIDENT,
              },
            },
          });
          const trailerId = dataTrailer.data.findOrCreateVehicleByPlate.id;
          processedVehicleIds.push(trailerId);
          vehiclesInput.push({
            vehicleId: trailerId,
            insurance: vehicle.insurerTrailer || null,
            policyNumber: vehicle.policyTrailer || null,
            isTrailer: true,
            isActive: false,
            // El backend resuelve el parentVehicleContextId en la tx.
            parentVehicleId: mainVehicleId,
          });
        }
      }
    }

    // Bloque C-4: SAVE_ACCIDENT (update path con id) sustituye UPDATE_ORDER +
    // UPDATE_ORDER_RELATIONSHIPS + UPDATE_ACCIDENT_ORDER por una mutación atómica
    // vía $transaction Prisma. Backend mantiene Order ↔ Accident coherentes —
    // elimina deadlock P2034 que motivaba skipOrderSync. UPDATE_NEW_ATTENTION_ACCIDENT
    // queda legacy (entidad separada AttentionAccident).
    // Fold (Bloque B2): SAVE_ACCIDENT debe dispararse también cuando hay fotos
    // nuevas que plegar (aunque no haya cambios en campos del Order/Accident),
    // porque la creación de Image ahora viaja dentro de `input.images`.
    const hasNewImages = newImages.length > 0;
    const hasOrderChanges =
      accidentDataDifference?.capitol ||
      accidentDataDifference?.subCapitol ||
      accidentDataDifference?.predefine ||
      accidentDataDifference?.concession ||
      accidentDataDifference?.road ||
      accidentDataDifference?.description ||
      accidentDataDifference?.notes ||
      accidentDataDifference?.registeredByUserId ||
      accidentDataDifference?.registerInitDate ||
      accidentDataDifference?.pkInit ||
      accidentDataDifference?.pkEnd ||
      accidentDataDifference?.direction ||
      accidentDataDifference?.margin ||
      accidentDataDifference?.lane ||
      accidentDataDifference?.track ||
      basicInfoDifference.laneCutting !== undefined ||
      basicInfoDifference.policeReport !== undefined ||
      basicInfoDifference.policeReportNumber ||
      basicInfoDifference.roadConditionDescription ||
      vehiclesDifference.differences?.damages !== undefined ||
      hasNewImages ||
      // FOLD: el save también corre si cambian vehículos/contexts o satélites.
      vehiclesInput !== undefined ||
      satellitesChanged;

    await Promise.all([
      hasOrderChanges
        ? phaseClient.mutate({
            mutation: SAVE_ACCIDENT,
            variables: {
              input: {
                // Idempotencia online (RetryLink reintenta con las mismas
                // variables → mismo clientOpId → el backend no duplica).
                clientOpId: opts?.clientOpId ?? crypto.randomUUID(),
                id: accidentId,
                registerInitDate: accidentDataDifference?.registerInitDate || undefined,
                pkInitKm: accidentDataDifference?.pkInit
                  ? parseInt(accidentDataDifference.pkInit.split('+')[0])
                  : undefined,
                pkInitMeter: accidentDataDifference?.pkInit
                  ? parseInt(accidentDataDifference.pkInit.split('+')[1])
                  : undefined,
                pkEndKm: accidentDataDifference?.pkEnd
                  ? parseInt(accidentDataDifference.pkEnd.split('+')[0])
                  : undefined,
                pkEndMeter: accidentDataDifference?.pkEnd
                  ? parseInt(accidentDataDifference.pkEnd.split('+')[1])
                  : undefined,
                track: accidentDataDifference.track || undefined,
                direction: accidentDataDifference.direction || undefined,
                margin: accidentDataDifference.margin || undefined,
                lane: accidentDataDifference.lane || undefined,
                concessionId: accidentDataDifference?.concession || undefined,
                roadId: accidentDataDifference?.road || undefined,
                capitolId: accidentDataDifference?.capitol || undefined,
                subCapitolId: accidentDataDifference?.subCapitol || undefined,
                predefinedOrderId: accidentDataDifference?.predefine || undefined,
                description: accidentDataDifference?.description || undefined,
                notes: accidentDataDifference?.notes || undefined,
                // Operario que registra editable (no obligatorio). Solo viaja
                // cuando el diff lo detecta; el backend valida tenant y nunca
                // obliga en update.
                registeredByUserId:
                  accidentDataDifference?.registeredByUserId || undefined,
                damages: vehiclesDifference.differences?.damages || undefined,
                laneCutting:
                  basicInfoDifference.laneCutting !== undefined
                    ? basicInfoDifference.laneCutting
                    : undefined,
                policeReport:
                  basicInfoDifference.policeReport !== undefined
                    ? basicInfoDifference.policeReport
                    : undefined,
                policeReportNumber: basicInfoDifference.policeReportNumber || undefined,
                roadConditionDescription: basicInfoDifference.roadConditionDescription || undefined,
                // FOLD: satélites plegados (antes en el
                // UPDATE_ACCIDENT_ORDER_RELATIONSHIPS post-save).
                surveillanceBodyId:
                  basicInfoDifference?.surveillanceBody || undefined,
                mediumKnowledgeId:
                  basicInfoDifference?.mediumKnowledge || undefined,
                weatherId: basicInfoDifference?.weather || undefined,
                surfaceConditionId:
                  basicInfoDifference?.surfaceCondition || undefined,
                luminosityId: basicInfoDifference?.luminosity || undefined,
                conditionsDrivingId:
                  basicInfoDifference?.conditionsDriving || undefined,
                // FOLD contexts: set completo con snapshots + trailers;
                // undefined = no tocar vehículos.
                vehicles: vehiclesInput,
                // Fold (Bloque B2): creación de Image plegada en saveAccident.
                // Backend crea las Image dentro de su $transaction. Solo fotos
                // con storageKey (subidas a Hetzner OK); las que fallaron offline
                // van por la cola diferida, no aquí.
                images: hasNewImages ? newImages : undefined,
              },
            },
          })
        : undefined,
      basicInfoDifference.noticeConservationCenterDate ||
      basicInfoDifference.arrivalConservationCenterDate ||
      basicInfoDifference.departureConservationCenterDate ||
      basicInfoDifference.noticeSquadBoysDate ||
      basicInfoDifference.arrivalSquadBoysDate ||
      basicInfoDifference.departureSquadBoysDate ||
      basicInfoDifference.noticeRoadTeamDate ||
      basicInfoDifference.arrivalRoadTeamDate ||
      basicInfoDifference.departureRoadTeamDate ||
      basicInfoDifference.noticeControlCenterDate ||
      basicInfoDifference.arrivalControlCenterDate ||
      basicInfoDifference.departureControlCenterDate ||
      basicInfoDifference.noticeAmbulanceCraneAssistanceDate ||
      basicInfoDifference.arrivalAmbulanceCraneAssistanceDate ||
      basicInfoDifference.departureAmbulanceCraneAssistanceDate ||
      basicInfoDifference.otherName ||
      basicInfoDifference.noticeOtherDate ||
      basicInfoDifference.arrivalOtherDate ||
      basicInfoDifference.departureOtherDate
        ? phaseClient.mutate({
            mutation: UPDATE_NEW_ATTENTION_ACCIDENT,
            variables: {
              input: {
                id: attentionAccidentId,
                noticeConservationCenterDate:
                  basicInfoDifference?.noticeConservationCenterDate
                    ? basicInfoDifference.noticeConservationCenterDate
                    : undefined,
                arrivalConservationCenterDate:
                  basicInfoDifference?.arrivalConservationCenterDate
                    ? basicInfoDifference.arrivalConservationCenterDate
                    : undefined,
                departureConservationCenterDate:
                  basicInfoDifference?.departureConservationCenterDate
                    ? basicInfoDifference.departureConservationCenterDate
                    : undefined,
                noticeSquadBoysDate: basicInfoDifference?.noticeSquadBoysDate
                  ? basicInfoDifference.noticeSquadBoysDate
                  : undefined,
                arrivalSquadBoysDate: basicInfoDifference?.arrivalSquadBoysDate
                  ? basicInfoDifference.arrivalSquadBoysDate
                  : undefined,
                departureSquadBoysDate:
                  basicInfoDifference?.departureSquadBoysDate
                    ? basicInfoDifference.departureSquadBoysDate
                    : undefined,
                noticeRoadTeamDate: basicInfoDifference?.noticeRoadTeamDate
                  ? basicInfoDifference.noticeRoadTeamDate
                  : undefined,
                arrivalRoadTeamDate: basicInfoDifference?.arrivalRoadTeamDate
                  ? basicInfoDifference.arrivalRoadTeamDate
                  : undefined,
                departureRoadTeamDate:
                  basicInfoDifference?.departureRoadTeamDate
                    ? basicInfoDifference.departureRoadTeamDate
                    : undefined,
                noticeControlCenterDate:
                  basicInfoDifference?.noticeControlCenterDate
                    ? basicInfoDifference.noticeControlCenterDate
                    : undefined,
                arrivalControlCenterDate:
                  basicInfoDifference?.arrivalControlCenterDate
                    ? basicInfoDifference.arrivalControlCenterDate
                    : undefined,
                departureControlCenterDate:
                  basicInfoDifference?.departureControlCenterDate
                    ? basicInfoDifference.departureControlCenterDate
                    : undefined,
                noticeAmbulanceCraneAssistanceDate:
                  basicInfoDifference?.noticeAmbulanceCraneAssistanceDate
                    ? basicInfoDifference.noticeAmbulanceCraneAssistanceDate
                    : undefined,
                arrivalAmbulanceCraneAssistanceDate:
                  basicInfoDifference?.arrivalAmbulanceCraneAssistanceDate
                    ? basicInfoDifference.arrivalAmbulanceCraneAssistanceDate
                    : undefined,
                departureAmbulanceCraneAssistanceDate:
                  basicInfoDifference?.departureAmbulanceCraneAssistanceDate
                    ? basicInfoDifference.departureAmbulanceCraneAssistanceDate
                    : undefined,
                otherName: basicInfoDifference?.otherName
                  ? basicInfoDifference.otherName
                  : undefined,
                noticeOtherDate: basicInfoDifference?.noticeOtherDate
                  ? basicInfoDifference.noticeOtherDate
                  : undefined,
                arrivalOtherDate: basicInfoDifference?.arrivalOtherDate
                  ? basicInfoDifference.arrivalOtherDate
                  : undefined,
                departureOtherDate: basicInfoDifference?.departureOtherDate
                  ? basicInfoDifference.departureOtherDate
                  : undefined,
              },
            },
          })
        : undefined,
      // Bloque C-4: el bloque UPDATE_ACCIDENT_ORDER legacy se ha colapsado en la
      // primera mutación SAVE_ACCIDENT del Promise.all (campos accident-only:
      // direction/margin/lane/track/laneCutting/policeReport/damages, también
      // incluidos en SaveAccidentInput).
    ]);

    // FOLD: los bloques de switch-off (DELETE_ALL_VEHICLES +
    // DELETE_ALL_VEHICLE_CONTEXTS + relationships:[]) y el procesamiento por
    // diffs se ELIMINARON — el reconcile de `saveAccident.input.vehicles`
    // (arriba, dentro de la tx) cubre ambos casos.
    // FOLD: la fase de gestión de relaciones (GET_VEHICLE_BY_PLATE por
    // matrícula + DELETE_VEHICLE_ACCIDENT_CONTEXT trailers/principales +
    // UPDATE_ACCIDENT_ORDER_RELATIONSHIPS con satélites) se ELIMINÓ — todo
    // viaja dentro del saveAccident (tx) de arriba.

    // Refetch de los vehículos tocados (refresco de caché para la UI).
    const unresolvedPromiseVehicles: Promise<ApolloQueryResult<object>>[] = [];
    for (const vehicleId of processedVehicleIds) {
      unresolvedPromiseVehicles.push(
        phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_VEHICLE_BY_ID,
          variables: { id: vehicleId },
        })
      );
    }

    // Escrituras confirmadas: lo que queda son refrescos de caché (fuera del
    // plazo a propósito). Un fallo de red de aquí en adelante NO re-encola:
    // repetiría lo ya guardado (E7).
    writesCommitted = true;
    deadline.clear();

    // Resolve all pledges in parallel
    logMain('🔄 Iniciando refrescado final de caché antes del redirect...');
    logMain(
      `📊 IDs a refrescar: Order(${orderId}), Accident(${accidentId}), AttentionAccident(${attentionAccidentId})`
    );

    const refreshStart = performance.now();
    await Promise.all([
      client
        .query({
          fetchPolicy: 'network-only',
          query: GET_ORDER_BY_ID,
          variables: { id: orderId },
        })
        .then(() => {
          logCache('✅ GET_ORDER_BY_ID refrescado correctamente');
        }),
      client
        .query({
          fetchPolicy: 'network-only',
          query: GET_ACCIDENT_BY_ID,
          variables: { id: accidentId },
        })
        .then(() => {
          logCache('✅ GET_ACCIDENT_BY_ID refrescado correctamente');
        }),
      // 🎯 CRÍTICO: Refrescar contextos de vehículos para actualizar la UI
      client
        .query({
          fetchPolicy: 'network-only',
          query: GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
          variables: { accidentId: accidentId },
        })
        .then((result) => {
          logCache(
            '✅ GET_VEHICLE_CONTEXTS_BY_ACCIDENT refrescado correctamente'
          );
          logCache(
            `📋 Contextos obtenidos: ${
              result.data.vehicleContextsByAccident?.length || 0
            } contextos`
          );
        }),
      ...unresolvedPromiseVehicles,

      client
        .query({
          fetchPolicy: 'network-only',
          query: GET_ATTENTION_ACCIDENT_BY_ID,
          variables: {
            id: attentionAccidentId,
          },
        })
        .then(() => {
          logCache('✅ GET_ATTENTION_ACCIDENT_BY_ID refrescado correctamente');
        }),
    ]);

    const refreshEnd = performance.now();
    logMain(
      `⏱️ Refrescado de caché completado en ${(
        refreshEnd - refreshStart
      ).toFixed(2)}ms`
    );

    // Solo lo de esta edición (copias, croquis en curso y sus fotos nuevas).
    // Antes `removeNewAccidentStorage` tiraba también el alta de accidente a
    // medias de la pestaña y vaciaba las carpetas compartidas `accident` y
    // `canvas-accident` (auditoría de accidentes § 6). El contexto de
    // navegación se queda: decide a dónde se vuelve.
    await releaseAccidentEditDraft(form, orderId, { sketchFileName: ownSketchFile });
    logMain('🧹 Borrador de la edición limpiado');

    // A la campaña o a la OT de la que se vino si el contexto es de ESTE
    // accidente; si no, al padre que dio el servidor (ver
    // `resolveAccidentEditRedirect`).
    const redirectUrl = resolveAccidentEditRedirect({
      orderId,
      navigationContext: readAccidentNavigationContext(form.store),
      serverParent,
    });

    logMain(`🎉 PROCESO DE GUARDADO COMPLETADO EXITOSAMENTE`);
    logMain(`🔀 Redirigiendo a: ${redirectUrl}`);
    logMain(
      `📍 Estado de caché antes del redirect: ${
        Object.keys(client.cache.extract()).length
      } entradas`
    );

    return redirect(redirectUrl);
  } catch (error: any) {
    // RED DEGRADADA (E5): el plazo de la fase venció o cayó el transporte antes
    // de confirmar escrituras. Se encola lo mismo que la rama sin red y se
    // avisa. Sin esto, con cobertura mala este guardado esperaba sin techo.
    if (!opts?.isReplay && !writesCommitted && isTransportFailure(error) && !isOfflineQueuedError(error)) {
      logMain('⌛ Plazo de la fase vencido o transporte caído — encolando');
      const { redirectTo } = await queueAccidentEdit(
        orderId,
        accidentDataForm.roid ?? '',
        form,
        { photosHandedOff, serverParent },
      );
      window.dispatchEvent(new CustomEvent('offline-accident-queued'));
      throw createOfflineQueuedError({ redirectTo });
    }
    // C-11.8: si la mutation SaveAccident se encoló offline, OfflineQueueLink
    // lanza un Error señalizando "queued". No es un fallo real — Apollo
    // replay-eará la mutation al reconectar. Tratamos como éxito: limpiar
    // sessionStorage, emitir evento UI y redirigir como en el happy path.
    if (isOfflineQueuedError(error)) {
      if (opts?.isReplay) {
        // En replay, fingir éxito aquí haría que el sync manager borrara la op
        // compound sin que SAVE_ACCIDENT se hubiera ejecutado realmente —
        // resurrección del bug original (pérdida silenciosa de datos). Al
        // re-lanzar, el catch de syncCompoundQueue (isValidationError=false
        // para errores offlineQueued/red) lo trata como transient → la op se
        // conserva y reintenta con el MISMO clientOpId → converge cuando el
        // reintento sí complete la mutation.
        throw error;
      }
      logMain('📦 Mutation encolada offline — tratando como éxito');
      // Solo lo de esta edición; el contexto decide el destino (ver el
      // camino feliz).
      await releaseAccidentEditDraft(form, orderId, { sketchFileName: ownSketchFile });
      const redirectUrl = resolveAccidentEditRedirect({
        orderId,
        navigationContext: readAccidentNavigationContext(form.store),
        serverParent,
      });

      window.dispatchEvent(new CustomEvent('offline-accident-queued'));
      return redirect(redirectUrl);
    }

    logError('❌ ERROR EN PROCESO DE GUARDADO:', error);
    logError('📊 Error stack:', error.stack);
    logError('📊 Error name:', error.name);
    logError('📊 Error message:', error.message);

    // Intentar refrescar datos básicos en caso de error
    if (orderId) {
      logError('🔄 Intentando refrescar datos después del error...');
      try {
        await phaseClient.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: orderId,
          },
          fetchPolicy: 'network-only',
        });
        logError('✅ Datos básicos refrescados exitosamente tras error');
      } catch (refreshError) {
        logError('❌ Error adicional refrescando datos:', refreshError);
      }
    }

    Sentry.captureException(error);
    // Re-lanzar: antes `return null` silencioso → el usuario no sabía si se
    // guardó (clase del incidente At.0001979.0008.02). El catch del action /
    // errorElement de la ruta muestra el fallo.
    throw error;
  }
};
